import React from 'react'
import { StyleSheet, View, Text, LayoutChangeEvent } from 'react-native'
import PropTypes from 'prop-types'
import { IObject } from 'interfaces'
import { BaseObjectProps } from '../BaseObject'
import TableScroll from './TableScroll'
import PaginatedFetchComponent, {
  connectPaginatedFetch,
  PaginatedFetchProps,
} from './PaginatedFetchComponent'
import { getBindingData } from './utils'
import { type BindingDataItem } from './TableTypes'
import TableRow, { Row } from './TableRow'
import PaginationControls from './PaginationControls'
import { makeTheme } from './theme'
import TableHeaderBackground from './TableHeaderBackground'

interface TableObjectProps extends BaseObjectProps {
  bindingData: BindingDataItem[]
  object: IObject
}

type TableState = {
  page: number
  headerWidth: number | null
  headerHeight: number | null
}

const DEFAULT_PAGE_LIMIT = 10
const PREFETCH_NEXT_PAGE = true

type Props = TableObjectProps & PaginatedFetchProps
class Table extends PaginatedFetchComponent<Props, TableState> {
  constructor(props: Props) {
    super(props)

    this.state = { page: 0, headerWidth: null, headerHeight: null }
  }

  static childContextTypes = {
    getBindings: PropTypes.func,
    getStore: PropTypes.func,
    getParams: PropTypes.func,
  }

  getPageSize() {
    const { object } = this.props
    const { attributes } = object

    const pageSize = attributes?.pageSize ?? DEFAULT_PAGE_LIMIT

    return pageSize
  }

  getTableColumns() {
    const { bindingData, object } = this.props
    const fields = object?.attributes.fields

    if (bindingData && fields) {
      const { columns } = getBindingData(fields, bindingData)

      return columns
    }

    return []
  }

  getTableDataRowsForPage(page: number, pageSize: number) {
    const { bindingData, object } = this.props
    const fields = object?.attributes.fields

    if (bindingData && fields) {
      const { data } = getBindingData(fields, bindingData)

      let pageData = data

      if (this.isManuallyPaginated()) {
        pageData = data.slice(page * pageSize, (page + 1) * pageSize)
      }

      return pageData
    }

    return []
  }

  getTableDataRows() {
    const { page } = this.state
    const pageSize = this.getPageSize()
    return this.getTableDataRowsForPage(page, pageSize)
  }

  renderPaginationControls() {
    // Only render paginated controls for manually paginated tables (external collections aren't "manually" paginated)
    if (!this.isManuallyPaginated()) {
      return null
    }

    const { page } = this.state
    const { object } = this.props

    const pageSize = this.getPageSize()

    const goToNextPage = () => {
      const { dataBinding } = object

      if (!dataBinding) {
        throw new Error(
          `Tried to fetch data from Table without dataBinding. Object: ${object.id}`
        )
      }

      const nextPage = page + 1
      const nextPageDataLoaded = this.getTableDataRowsForPage(
        nextPage,
        pageSize
      )

      if (nextPageDataLoaded.length === 0) {
        this.fetchPageData(nextPage, pageSize)
      }

      if (PREFETCH_NEXT_PAGE) {
        this.fetchPageData(nextPage + 1, pageSize)
      }

      this.setState({ page: nextPage })
    }

    const goToPreviousPage = () => {
      if (page !== 0) {
        this.setState({ page: page - 1 })
      }
    }

    const dataItemCount = this.getTableDataRows().length
    const nextEnabled = dataItemCount >= pageSize

    return (
      <PaginationControls
        nextDisabled={!nextEnabled}
        currentPage={page}
        goToNextPage={goToNextPage}
        goToPreviousPage={goToPreviousPage}
      />
    )
  }

  getDatasource = () => {
    const { object } = this.props
    const { dataBinding } = object

    return dataBinding?.source
  }

  setHeaderWidth = (event: LayoutChangeEvent) => {
    const { width: headerWidth } = event.nativeEvent.layout
    this.setState({ headerWidth })
  }

  setHeaderHeight = (event: LayoutChangeEvent) => {
    const { height: headerHeight } = event.nativeEvent.layout
    this.setState({ headerHeight })
  }

  render() {
    const data = this.getTableDataRows()
    const columns = this.getTableColumns()
    const datasource = this.getDatasource()

    if (!datasource || columns.length === 0 || data.length === 0) {
      return null
    }

    const { object } = this.props
    const theme = makeTheme(object)

    const tableMinWidth = columns.reduce((acc, c) => acc + c.style.minWidth, 0)

    return (
      <View
        style={[styles.tableContainer, theme.tableContainer]}
        onLayout={this.setHeaderWidth}
      >
        <TableHeaderBackground
          theme={theme}
          width={this.state.headerWidth}
          height={this.state.headerHeight}
        />
        <TableScroll horizontal>
          <View style={[styles.tableLayout, { minWidth: tableMinWidth }]}>
            <View
              style={[styles.header, theme.header]}
              onLayout={this.setHeaderHeight}
            >
              {columns.map(({ label, style }) => (
                <View style={[styles.headerCell, style]} key={label}>
                  <Text style={[styles.headerText, theme.headerText]}>
                    {label}
                  </Text>
                </View>
              ))}
            </View>
            {data.map((rowData: Row) => (
              <TableRow
                datasource={datasource}
                data={rowData}
                theme={theme}
                tableObject={object}
              />
            ))}
          </View>
        </TableScroll>
        {this.renderPaginationControls()}
      </View>
    )
  }
}

const styles = StyleSheet.create({
  tableContainer: {
    display: 'flex',
    flexDirection: 'column',
  },
  tableLayout: {
    width: '100%',
  },
  header: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    minHeight: 50,
  },
  headerCell: {
    flex: 1,
    alignItems: 'flex-start',
    paddingLeft: 20,
    paddingTop: 15,
    padding: 10,
  },
  headerText: {
    fontSize: 13,
    fontWeight: '600',
    lineHeight: 15.25,
  },
})

// @ts-expect-error https://github.com/AdaloHQ/runner/pull/873/files#r1656132138
export default connectPaginatedFetch(Table)
